import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import { PromotionWindowChip, RatingBadgeChip, Section, SectionCard, SectionHeader } from '@/components/common';
import { RichTextEditor } from '@/components/common/RichTextEditor/RichTextEditor';
import { RichTextReadOnly } from '@/components/common/RichTextEditor/RichTextReadOnly';
import { useUpdateNotesToTalentCommittee } from '@/queries/useAppraisalDetail';
import type {
  AppraisalDetailResponse,
  UpdateNotesToTalentCommitteeRequest,
} from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';
import { readOnlySectionVariant } from '@/auth/sectionReadOnlyStyle';

interface NotesToTalentCommitteeSectionProps {
  appraisalDetail: AppraisalDetailResponse;
  permissions: RecordAccess;
  validationTriggered?: boolean;
}

const errorWrapperSx = {
  border: '2px solid',
  borderColor: 'error.main',
  borderRadius: 1,
} as const;

const RATING_OPTIONS = [
  { value: 'BT', label: 'BT' },
  { value: 'OT', label: 'OT' },
  { value: 'AT', label: 'AT' },
  { value: 'O', label: 'O' },
];

export function NotesToTalentCommitteeSection({
  appraisalDetail,
  permissions,
  validationTriggered = false,
}: NotesToTalentCommitteeSectionProps) {
  const sectionAccess = permissions.sections.notesToTalentCommittee;
  const canEdit = sectionAccess?.access === 'edit';

  const updateMutation = useUpdateNotesToTalentCommittee(String(appraisalDetail.id));

  // Local state for optimistic updates
  const [localNotes, setLocalNotes] = useState<string>(appraisalDetail.notesToTalentCommittee ?? '');
  const [localRating, setLocalRating] = useState<string | null>(appraisalDetail.proposedRating);
  // #1089/#1095: bind to the Collator's own Proposed* columns, not the TC decision.
  const [localPromote, setLocalPromote] = useState<boolean>(appraisalDetail.proposedPromote);
  const [localPmi, setLocalPmi] = useState<boolean>(
    appraisalDetail.proposedPerformanceManagementIntervention
  );

  // Track previous server values for sync on cache invalidation
  const serverKey = useMemo(
    () =>
      JSON.stringify({
        notes: appraisalDetail.notesToTalentCommittee,
        rating: appraisalDetail.proposedRating,
        proposedPromote: appraisalDetail.proposedPromote,
        proposedPmi: appraisalDetail.proposedPerformanceManagementIntervention,
      }),
    [
      appraisalDetail.notesToTalentCommittee,
      appraisalDetail.proposedRating,
      appraisalDetail.proposedPromote,
      appraisalDetail.proposedPerformanceManagementIntervention,
    ]
  );

  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    setLocalNotes(appraisalDetail.notesToTalentCommittee ?? '');
    setLocalRating(appraisalDetail.proposedRating);
    setLocalPromote(appraisalDetail.proposedPromote);
    setLocalPmi(appraisalDetail.proposedPerformanceManagementIntervention);
  }

  if (!sectionAccess || sectionAccess.access === 'none') {
    return null;
  }

  // Single payload builder so every save path ships ALL section fields — a handler
  // overrides only the field it changed (mirrors ConfidentialNotesSection.saveCombined).
  const buildPayload = (
    overrides: Partial<UpdateNotesToTalentCommitteeRequest> = {}
  ): UpdateNotesToTalentCommitteeRequest => ({
    notesToTalentCommittee: localNotes || null,
    proposedRating: localRating as 'BT' | 'OT' | 'AT' | 'O' | null,
    proposedPromote: localPromote,
    proposedPerformanceManagementIntervention: localPmi,
    ...overrides,
  });

  const saveSection = (overrides: Partial<UpdateNotesToTalentCommitteeRequest> = {}) => {
    updateMutation.mutate({
      data: buildPayload(overrides),
      rowVersion: appraisalDetail.rowVersion,
    });
  };

  const handleNotesBlur = () => {
    saveSection();
  };

  const handleRatingChange = (event: SelectChangeEvent<string>) => {
    const newRating = event.target.value === '' ? null : (event.target.value as 'BT' | 'OT' | 'AT' | 'O');
    setLocalRating(newRating);
    saveSection({ proposedRating: newRating });
  };

  const handlePromoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setLocalPromote(newValue);
    saveSection({ proposedPromote: newValue });
  };

  const handlePmiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setLocalPmi(newValue);
    saveSection({ proposedPerformanceManagementIntervention: newValue });
  };

  const ratingError = validationTriggered && canEdit && localRating === null;

  return (
    <Section>
      <SectionHeader error={ratingError}>NOTES TO TALENT COMMITTEE</SectionHeader>

      <SectionCard error={ratingError}>
        {canEdit ? (
          <RichTextEditor
            content={localNotes}
            onChange={setLocalNotes}
            onBlur={handleNotesBlur}
            placeholder="Type here talent committee notes..."
            aria-label="Notes to Talent Committee"
          />
        ) : (
          <RichTextReadOnly
            content={appraisalDetail.notesToTalentCommittee ?? ''}
            variant={readOnlySectionVariant('notesToTalentCommittee', permissions.role)}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 6, mt: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Proposed Rating
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {canEdit ? (
                <FormControl
                  size="small"
                  error={ratingError}
                  sx={{
                    minWidth: 160,
                    ...(ratingError && errorWrapperSx),
                  }}
                  data-testid={ratingError ? 'field-error' : undefined}
                >
                  <Select
                    value={localRating ?? ''}
                    onChange={handleRatingChange}
                    displayEmpty
                    aria-label="Proposed Rating"
                    aria-invalid={ratingError || undefined}
                    renderValue={(selected) => selected || 'Select'}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {RATING_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <RatingBadgeChip code={appraisalDetail.proposedRating} />
              )}
              <PromotionWindowChip
                appraisalId={appraisalDetail.id}
                proposedRating={canEdit
                  ? (localRating as 'BT' | 'OT' | 'AT' | 'O' | null)
                  : appraisalDetail.proposedRating}
                isCurrentUserAppraisee={permissions.role === 'Appraisee'}
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Proposed Actions
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localPromote}
                    onChange={handlePromoteChange}
                    disabled={!canEdit}
                    inputProps={{ 'aria-label': 'Promote' }}
                  />
                }
                label="Promote"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localPmi}
                    onChange={handlePmiChange}
                    disabled={!canEdit}
                    inputProps={{ 'aria-label': 'Performance Management Intervention Required' }}
                  />
                }
                label="Performance Management Intervention Required"
              />
            </Box>
          </Box>
        </Box>
      </SectionCard>
    </Section>
  );
}
